import React, { useState, useEffect, useRef } from 'react';
import './FirePopUp.scss';
import axios from 'axios';
import Spinner from '../../Spinner';
import * as turf from '@turf/turf';
import mapboxgl from 'mapbox-gl';
import refreshIcon from '../../../assets/icons/refresh-icon.png';

export default function AIPrescribedFirePrediction({ map, setIs3D, getToken, latitude, longitude, fire }) {
  const [loading, setLoading] = useState(true);
  const [prediction, setPrediction] = useState(null);
  const [prescribedLine, setPrescribedLine] = useState(null);
  const [stage, setStage] = useState('API Connection Test');
  const [isViewing, setIsViewing] = useState(false);
  
  const pollingIntervalRef = useRef(null);
  const animationFrameRef = useRef(null);
  const popupRef = useRef(null);

  const getPrediction = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const { data } = await axios.get(`https://firewatch-api.space-eyes.com/manage-prescribed-fires?fire_id=${fire.fire_id}`, {
        headers: { 
          'Authorization': `Bearer ${token}` 
        }
      });
      
      // Handle different possible response structures
      let currentPrediction = null;
      if (data.prescribed_fire && Array.isArray(data.prescribed_fire)) {
        currentPrediction = data.prescribed_fire[0] ?? null;
      } else if (data.prescribed_fire) {
        currentPrediction = data.prescribed_fire;
      } else if (Array.isArray(data)) {
        currentPrediction = data[0] ?? null;
      } else if (data.id) {
        currentPrediction = data;
      }
      
      console.log('currentPrediction', currentPrediction);
      setPrediction(currentPrediction);
      
      // Don't plot the line automatically - only when user clicks "View"
      
      // If prediction is pending, start polling
      if (currentPrediction?.status === 'pending') {
        pollPredictionStatus();
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('error fetching prescribed fire prediction', err);
      setPrediction(null);
      setLoading(false);
    }
  };
  
  const requestPrediction = async () => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    setLoading(true);
    try {
      const token = await getToken();
      const { data } = await axios.post(
        `https://firewatch-api.space-eyes.com/manage-prescribed-fires`,
        { fire_id: fire.fire_id, latitude: fire.latitude, longitude: fire.longitude },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Handle different possible response structures
      let currentPrediction = null;
      if (data.prescribed_fire && Array.isArray(data.prescribed_fire)) {
        currentPrediction = data.prescribed_fire[0] ?? null;
      } else if (data.prescribed_fire) {
        currentPrediction = data.prescribed_fire;
      } else if (Array.isArray(data)) {
        currentPrediction = data[0] ?? null;
      } else if (data.id) {
        currentPrediction = data;
      }
      
      setPrediction(currentPrediction);
      
      // If prediction is pending, start polling until it's complete or failed
      if (currentPrediction?.status === 'pending') {
        pollPredictionStatus();
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('error requesting prescribed fire prediction', err);
      setLoading(false);
    }
  };

  const pollPredictionStatus = async () => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const token = await getToken();
        const { data } = await axios.get(
          `https://firewatch-api.space-eyes.com/manage-prescribed-fires?fire_id=${fire.fire_id}`,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        
        // Handle different possible response structures
        let currentPrediction = null;
        if (data.prescribed_fire && Array.isArray(data.prescribed_fire)) {
          currentPrediction = data.prescribed_fire[0] ?? null;
        } else if (data.prescribed_fire) {
          currentPrediction = data.prescribed_fire;
        } else if (Array.isArray(data)) {
          currentPrediction = data[0] ?? null;
        } else if (data.id) {
          currentPrediction = data;
        }
        
        console.log('polling currentPrediction', currentPrediction);
        setPrediction(currentPrediction);
        
        // Don't plot the line automatically - only when user clicks "View"
        
        // Stop polling if prediction is complete or failed
        if (currentPrediction?.status === 'complete' || currentPrediction?.status === 'failed') {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setLoading(false);
        }
      } catch (err) {
        console.error('error polling prediction status', err);
        // Don't stop polling on 401 errors, just log them
        if (err.response?.status === 401) {
          console.warn('Authentication error during polling, continuing...');
          return;
        }
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setLoading(false);
      }
    }, 5000); // Poll every 5 seconds
  };

  const plotPrescribedFireLine = (geom) => {
    if (!map.current || !geom) return;

    try {
      // Parse the geometry
      const geometry = typeof geom === 'string' ? JSON.parse(geom) : geom;
      
      // Clean up existing layers and sources
      const existingLayers = map.current.getStyle().layers.filter((layer) => 
        layer.id.includes('prescribed-fire-line')
      );
      existingLayers.forEach((layer) => {
        if (map.current.getLayer(layer.id)) {
          map.current.removeLayer(layer.id);
        }
      });

      const existingSources = Object.keys(map.current.getStyle().sources).filter((sourceId) => 
        sourceId.includes('prescribed-fire-line')
      );
      existingSources.forEach((sourceId) => {
        if (map.current.getSource(sourceId)) {
          map.current.removeSource(sourceId);
        }
      });

      // Create the GeoJSON feature
      const geojson = {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: geometry,
          properties: {
            id: prediction?.id || 'prescribed-fire',
            title: 'AI Prescribed Fire Prediction',
            description: 'This boundary represents an AI-generated prediction for where a controlled burn could be safely conducted to manage vegetation and reduce wildfire risk.'
          }
        }]
      };

      // Add source
      const sourceId = 'prescribed-fire-line-source';
      map.current.addSource(sourceId, {
        type: 'geojson',
        data: geojson
      });

      // Add fill layer with solid color
      map.current.addLayer({
        id: 'prescribed-fire-line-fill',
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': '#22c55e',
          'fill-opacity': 0.8
        }
      });

      // Add click handler for popup
      map.current.on('click', 'prescribed-fire-line-fill', (e) => {
        const coordinates = e.lngLat;
        const properties = e.features[0].properties;
        
        // Remove existing popup
        if (popupRef.current) {
          popupRef.current.remove();
        }
        
        // Create new popup
        popupRef.current = new mapboxgl.Popup({
          offset: 25,
          maxWidth: '400px',
          className: 'popup'
        })
          .setLngLat(coordinates)
          .setHTML(`
            <div>
              <div class="alert-popup-title">
                ${properties.title}
              </div>
              <div class="alert-popup-row">
                <div class="alert-popup-row-name">Description:</div>
                <div class="alert-popup-row-value">
                  ${properties.description}
                </div>
              </div>
              <div class="alert-popup-row">
                <div class="alert-popup-row-name">Status:</div>
                <div class="alert-popup-row-value">
                   Complete
                </div>
              </div>
            </div>
          `)
          .addTo(map.current);
      });

      // Change cursor on hover
      map.current.on('mouseenter', 'prescribed-fire-line-fill', () => {
        map.current.getCanvas().style.cursor = 'pointer';
      });

      map.current.on('mouseleave', 'prescribed-fire-line-fill', () => {
        map.current.getCanvas().style.cursor = '';
      });

      // Fit map to the prescribed fire area
      const bbox = turf.bbox(geojson);
      map.current.fitBounds(bbox, { padding: 200, maxZoom: 16 });

      // Show popup automatically when line appears
      setTimeout(() => {
        if (map.current && geojson.features.length > 0) {
          const coordinates = turf.centroid(geojson.features[0]).geometry.coordinates;
          const properties = geojson.features[0].properties;
          
          // Remove existing popup
          if (popupRef.current) {
            popupRef.current.remove();
          }
          
          // Create new popup
          popupRef.current = new mapboxgl.Popup({
            offset: 25,
            maxWidth: '400px',
            className: 'popup'
          })
            .setLngLat(coordinates)
            .setHTML(`
              <div>
                <div class="alert-popup-title">
                  ${properties.title}
                </div>
                <div class="alert-popup-row">
                  <div class="alert-popup-row-name">Description:</div>
                  <div class="alert-popup-row-value">
                    ${properties.description}
                  </div>
                </div>
                <div class="alert-popup-row">
                  <div class="alert-popup-row-name">Status:</div>
                  <div class="alert-popup-row-value">
                     Complete
                  </div>
                </div>
              </div>
            `)
            .addTo(map.current);
        }
      }, 1000); // Show popup after 1 second

    } catch (error) {
      console.error('Error plotting prescribed fire line:', error);
    }
  };

  useEffect(() => { 
    getPrediction();
  }, []);

  // Cleanup polling interval and animation frame on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);
  
  useEffect(() => { 
    // Cleanup function to remove map layers when component unmounts
    return () => {
      // Cancel any running animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      if (map.current) {
        const layers = ['prescribed-fire-line-fill'];
        const sources = ['prescribed-fire-line-source'];
        
        layers.forEach(layerId => {
          try {
            if (map.current.getLayer(layerId)) {
              map.current.removeLayer(layerId);
            }
          } catch (e) {
            console.warn(`Error removing layer ${layerId}:`, e);
          }
        });
        
        sources.forEach(sourceId => {
          try {
            if (map.current.getSource(sourceId)) {
              map.current.removeSource(sourceId);
            }
          } catch (e) {
            console.warn(`Error removing source ${sourceId}:`, e);
          }
        });
      }
    };
  }, []);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '2rem',
        textAlign: 'center',
        color: '#666'
      }}>
        <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
          {prediction?.status === 'pending' ? 'Processing Prescribed Fire Prediction' : 'Loading Prescribed Fire Prediction'}
        </div>
        <div style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
          {prediction?.status === 'pending' 
            ? 'Our AI is analyzing optimal conditions for prescribed fire management. This may take a few minutes.' 
            : 'Fetching prediction data...'}
        </div>
        <Spinner />
      </div>
    );
  }

  if (!isViewing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '0.5rem', marginTop: '0.3rem' }}>
        <div style={{ fontSize: '2rem', color: 'black' }}>AI Prescribed Fire Prediction</div>
        <div style={{ fontSize: '0.8rem', color: 'black' }}>
          Our AI Prescribed Fire Prediction system analyzes optimal conditions and timing for controlled burns to manage vegetation, reduce wildfire risk, and restore ecosystem health. By processing weather patterns, fuel moisture levels, air quality conditions, and historical burn data, the system generates detailed recommendations for safe and effective prescribed fire operations.
        </div>
        {!prediction ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '1.5rem',
            textAlign: 'center',
            color: '#666',
            backgroundColor: 'rgba(229, 229, 229, 0.7)',
            borderRadius: '8px',
            border: '1px solid #ddd',
            gap: '1rem'
          }}>
            <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.3rem', color: '#333' }}>
              What to Expect
            </div>
            <div style={{ fontSize: '0.85rem', lineHeight: '1.4', marginBottom: '0.5rem' }}>
              Our AI will analyze current conditions, weather forecasts, fuel moisture, and air quality to generate optimal prescribed fire recommendations for this location.
            </div>
            <button
              style={{ 
                background: 'rgba(54, 124, 255, 0.9)', 
                border: '1px solid #367cff', 
                paddingTop: '0.7rem', 
                paddingBottom: '0.7rem', 
                paddingLeft: '1.5rem',
                paddingRight: '1.5rem',
                fontSize: '0.8rem', 
                color: 'white', 
                fontWeight: 'bold',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(54, 124, 255, 0.3)'
              }}
              onClick={requestPrediction}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(54, 124, 255, 1)';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(54, 124, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(54, 124, 255, 0.9)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(54, 124, 255, 0.3)';
              }}
            >
              Request Prescribed Fire Prediction
            </button>
          </div>
        ) : prediction.status === 'pending' ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '2rem',
            textAlign: 'center',
            color: '#666',
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            borderRadius: '8px',
            border: '1px solid #ffc107'
          }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#856404' }}>
              🔥 Prediction in Progress
            </div>
            <div style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
              Our AI is processing your prescribed fire prediction. This typically takes 2-5 minutes.
            </div>
            <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
              You can leave this tab and return later - we'll keep checking for updates.
            </div>
          </div>
        ) : prediction.status === 'complete' ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '1.5rem',
            textAlign: 'center',
            color: '#666',
            backgroundColor: 'rgba(40, 167, 69, 0.1)',
            borderRadius: '8px',
            border: '1px solid #28a745',
            gap: '1rem'
          }}>
            <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.3rem', color: '#155724' }}>
              Prediction Ready
            </div>
            <div style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              Your AI-generated prescribed fire prediction is complete and ready to view.
            </div>
            <button
              style={{ 
                background: 'rgba(40, 167, 69, 0.9)', 
                border: '1px solid #28a745', 
                paddingTop: '0.7rem', 
                paddingBottom: '0.7rem', 
                paddingLeft: '1.5rem',
                paddingRight: '1.5rem',
                fontSize: '0.8rem', 
                color: 'white', 
                fontWeight: 'bold',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)'
              }}
              onClick={() => {
                setIsViewing(true);
                // Plot the prescribed fire line when viewing
                if (prediction?.geom) {
                  plotPrescribedFireLine(prediction.geom);
                }
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(40, 167, 69, 1)';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(40, 167, 69, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(40, 167, 69, 0.9)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(40, 167, 69, 0.3)';
              }}
            >
              View Prescribed Fire Prediction
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <button
              style={{ margin: 0, background: 'rgba(220, 53, 69, 0.9)', width: '100%', border: '1px solid #dc3545', paddingTop: '0.9rem', paddingBottom: '0.9rem', fontSize: '0.85rem', color: 'white', fontWeight: 'bold' }}
              className="fires-page-content-add-fire-btn"
              onClick={requestPrediction}
            >
              🔄 Retry Prescribed Fire Prediction
            </button>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              padding: '1rem',
              textAlign: 'center',
              color: '#666',
              backgroundColor: 'rgba(220, 53, 69, 0.1)',
              borderRadius: '8px',
              border: '1px solid #dc3545'
            }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.3rem', color: '#721c24' }}>
                Prediction Failed
              </div>
              <div style={{ fontSize: '0.8rem' }}>
                There was an issue generating your prediction. Please try again.
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Creative description */}
      <div style={{ 
        padding: '1.5rem', 
        backgroundColor: 'rgba(34, 139, 34, 0.1)', 
        borderRadius: '12px', 
        marginBottom: '1rem',
        // border: '2px solid #22c55e',
        boxShadow: '0 4px 12px rgba(34, 139, 34, 0.2)'
      }}>
        <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.8rem', color: '#15803d', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Prescribed Fire Line Visualization
        </div>
        <div style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#166534', marginBottom: '1rem' }}>
          You are now viewing an AI-generated prescribed fire prediction line on the map. This glowing green boundary represents our best estimate for where a controlled burn could be safely conducted to manage vegetation and reduce wildfire risk.
        </div>
        
        <div style={{ 
          padding: '1rem', 
          backgroundColor: 'rgba(255, 193, 7, 0.15)', 
          borderRadius: '8px', 
          border: '1px solid #f59e0b',
          marginBottom: '1rem'
        }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            Important Disclaimer
          </div>
          <div style={{ fontSize: '0.85rem', lineHeight: '1.5', color: '#78350f' }}>
            <strong>This prediction is for informational purposes only.</strong> AI models can be wrong, and fire behavior is inherently unpredictable. Always consult with certified fire management professionals and follow local regulations before conducting any prescribed fire operations. Weather conditions, fuel moisture, and other factors can change rapidly and affect fire behavior.
          </div>
        </div>
        
        <div style={{ fontSize: '0.85rem', color: '#166534', fontStyle: 'italic' }}>
          💡 <strong>Tip:</strong> Click on the green line on the map to see detailed information about this prediction.
        </div>
      </div>
      
      {/* Back button */}
      <div 
        className='fire-popup-back-btn' 
        style={{ marginTop: '1rem', marginBottom: '1rem' }}
        onClick={() => setIsViewing(false)}
      >
        ← Back to Prediction Status
      </div>
    </div>
  );
}